const HOUR_MS = 60 * 60 * 1000;
const MONEY_FIELDS = ['rentalAmount', 'convenienceFee', 'totalPaid', 'payableByFreac'];

// This is a report projection. Booking IDs, payment references and stored records stay intact.
export function itemizeBookingReport(bookings = []) {
  return bookings.flatMap((booking) => {
    const start = new Date(booking.startTime).getTime();
    const end = new Date(booking.endTime).getTime();
    if (booking.bookingType !== 'Court Booking' || !Number.isFinite(start) || !Number.isFinite(end) || end - start <= HOUR_MS) {
      return [{ ...booking, itemKey: `${booking.bookingType}-${booking.id}-${booking.startTime}` }];
    }
    const duration = end - start;
    const count = Math.ceil(duration / HOUR_MS);
    const supplied = booking.hourlyRentalAmounts;
    const hourlyAmounts = Array.isArray(supplied) && supplied.length === count
      && supplied.every(amount => typeof amount === 'number' && Number.isFinite(amount) && amount >= 0)
      && supplied.reduce((sum, amount) => sum + Math.round(Number(amount) * 100), 0) === Math.round(Number(booking.rentalAmount) * 100)
      ? supplied.map(Number) : null;
    const totalWeight = hourlyAmounts?.reduce((sum, amount) => sum + Math.round(amount * 100), 0) || duration;
    let cumulativeWeight = 0;
    const items = [];
    for (let index = 0; index < count; index++) {
      const slotStart = start + index * HOUR_MS;
      const slotEnd = Math.min(slotStart + HOUR_MS, end);
      const item = {
        ...booking,
        itemKey: `${booking.bookingType}-${booking.id}-${index}`,
        startTime: new Date(slotStart).toISOString(),
        endTime: new Date(slotEnd).toISOString(),
        slotNumber: index + 1,
        slotCount: count,
      };
      const previousWeight = cumulativeWeight;
      cumulativeWeight += hourlyAmounts && hourlyAmounts.some(amount => amount > 0)
        ? Math.round(hourlyAmounts[index] * 100) : slotEnd - slotStart;
      for (const field of MONEY_FIELDS) {
        if (booking[field] == null) continue;
        if (field === 'rentalAmount' && hourlyAmounts) { item[field] = hourlyAmounts[index]; continue; }
        // Rate-weighted allocation keeps discounts/fees consistent and preserves exact recorded totals.
        // Older API responses retain duration allocation until the API is upgraded.
        const cents = Math.round(Number(booking[field]) * 100);
        item[field] = (Math.round(cents * cumulativeWeight / totalWeight)
          - Math.round(cents * previousWeight / totalWeight)) / 100;
      }
      items.push(item);
    }
    return items;
  });
}

// Sum the original court records, not item counts or per-player Open Play registrations.
export function totalBookingHours(bookings = []) {
  return bookings.reduce((total, booking) => {
    if (booking.bookingType !== 'Court Booking') return total;
    const duration = new Date(booking.endTime).getTime() - new Date(booking.startTime).getTime();
    return Number.isFinite(duration) && duration > 0 ? total + duration / HOUR_MS : total;
  }, 0);
}
